// Visor de imágenes DENTRO de la app.
//
// Por qué existe: hasta ahora las imágenes de WhatsApp se abrían con <a target="_blank">. En el
// navegador de escritorio eso está bien, pero dentro de la app envuelta con Capacitor (y en la PWA
// del iPhone en pantalla completa) el enlace se abre en la MISMA webview, sin barra de direcciones
// ni botón de atrás: el usuario se queda atrapado en la imagen y tiene que matar la app.
//
// Aquí la imagen se muestra en una capa propia que se cierra de las cuatro maneras que la gente
// intenta: la X, tocar el fondo, la tecla Escape y el botón ATRÁS de Android (para eso se mete una
// entrada en el historial al abrir; el back del sistema hace history.back() y nosotros cerramos).
// El botón X va bajo el notch (safe-area) para que se pueda pulsar en el iPhone.
//
// (27-oct-2026, B-2254) LA FOTO SE AMPLÍA CON DOS DEDOS. El dueño: «en iPhone, cuando se abre un PDF o documento del estilo, no
// se puede pellizcar la pantalla para aumentar o disminuir». Antes un toque la ponía al 200 % y nada más; y dentro de la app
// el pellizco de la página está apagado (Capacitor sin `zoomEnabled`, y `maximum-scale=1.0` de B-838: utils/pellizco.js).
// Ahora la foto va en una zona con su propio zoom (utils/zonaAmpliable.jsx): dos dedos hacia donde están, doble toque,
// Ctrl+rueda y los botones − / +, hasta ×5, y un dedo se mueve por lo ampliado. Tocar fuera de la foto sigue cerrando.
import { useEffect, useCallback, useState, useRef } from 'react'
import { enLaAppNativa } from '../utils/verDocumento'
import { descargarFueraDeLaApp } from '../utils/descargaFuera'
import { useZonaAmpliable, ControlesZoom } from '../utils/zonaAmpliable'
import { t } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export default function VisorImagen({ src, alt, onClose }) {
  // La proporción de la foto (alto / ancho), en cuanto se carga: con ella la zona la deja entera en la pantalla.
  const [proporcion, setProporcion] = useState(0)
  const zona = useZonaAmpliable({ proporcion, centrarAlto: true, etiqueta: t('components.VisorImagen.pellizca_toca_dos_veces_o', '{{x}}: pellizca, toca dos veces o usa los botones para ampliar', { x: (alt || t('components.VisorImagen.imagen', 'Imagen')) }) })
  // Para no llamar a history.back() si la entrada que metimos ya no está (el back del sistema ya la
  // consumió). Sin esto, cerrar con la X tras un back retrocedería una pantalla de más.
  const nuestraEntrada = useRef(false)

  const cerrar = useCallback(() => {
    if (nuestraEntrada.current) {
      nuestraEntrada.current = false
      try { window.history.back(); return } catch { /* sin historial utilizable: cerramos a mano */ }
    }
    onClose?.()
  }, [onClose])

  useEffect(() => {
    if (!src) return undefined
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); cerrar() } }
    const onPop = () => { nuestraEntrada.current = false; onClose?.() }
    try { window.history.pushState({ visorImagen: true }, ''); nuestraEntrada.current = true } catch { /* si el navegador no deja, se cierra con la X */ }
    window.addEventListener('keydown', onKey)
    window.addEventListener('popstate', onPop)
    // Que el fondo no siga haciendo scroll mientras la imagen está abierta.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('popstate', onPop)
      document.body.style.overflow = overflow
      // Si el visor se desmonta por otra vía (cambio de conversación, navegación…), la entrada que
      // metimos en el historial se queda ahí y el siguiente "atrás" no haría nada visible.
      if (nuestraEntrada.current) { nuestraEntrada.current = false; try { window.history.back() } catch { /* nada que deshacer */ } }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  // Otra foto en el mismo visor: sin zoom, y la proporción la dirá la nueva al cargarse.
  useEffect(() => { setProporcion(0); zona.reiniciar() }, [src])   // eslint-disable-line react-hooks/exhaustive-deps

  if (!src) return null

  // Descargar/abrir fuera. En la app envuelta, `download` no siempre funciona en la webview: si
  // Capacitor está presente se usa su navegador del sistema, que sí trae su propio botón de cerrar.
  // (16-oct-2026) Sin ese plugin (la app de hoy no lo trae), el fichero va al navegador del teléfono (utils/descargaFuera): el
  // dueño, «pasa lo mismo con los documentos en el chat de equipo».
  const abrirFuera = (e) => {
    const B = typeof window !== 'undefined' && window.Capacitor?.Plugins?.Browser
    if (B) { e.preventDefault(); B.open({ url: src }).catch(() => {}); return }
    if (enLaAppNativa()) {
      e.preventDefault()
      fetch(src).then((r) => r.blob()).then((blob) => descargarFueraDeLaApp({ blob, nombre: alt || 'imagen' })).catch(() => {})
    }
  }

  return (
    <div
      onClick={cerrar}
      className="visor-oscuro"
      style={{
        position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(8,10,14,0.94)',
        display: 'flex', flexDirection: 'column', gap: 8,
        padding: 'max(10px, env(safe-area-inset-top)) 12px max(12px, var(--sa-abajo))',
        overscrollBehavior: 'contain',
      }}
    >
      {/* Barra superior: cerrar + descargar. Siempre por debajo del notch. */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', flexShrink: 0,
        }}
      >
        <button onClick={cerrar} aria-label={t('components.VisorImagen.cerrar', 'Cerrar')}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 999, border: 'none', background: 'rgba(255,255,255,0.16)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(6px)' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          {t('components.VisorImagen.cerrar', 'Cerrar')}
        </button>
        <a href={src} download={alt || 'imagen'} target="_blank" rel="noreferrer" onClick={abrirFuera} aria-label={t('components.VisorImagen.descargar', 'Descargar')} data-descarga-propia
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.16)', color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none', backdropFilter: 'blur(6px)' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          {t('components.VisorImagen.descargar', 'Descargar')}
        </a>
      </div>

      {/* La imagen, en la zona ampliable (B-2254): entera en la pantalla y, con dos dedos, el doble toque o los botones,
          hasta ×5. Tocar la foto no cierra; tocar fuera de ella, sí. */}
      <div {...zona.propsExterior}>
        <div {...zona.propsZona}>
          <div {...zona.propsMarco}>
            <div {...zona.propsContenido}>
              <img src={src} alt={alt || ''} draggable={false} onClick={e => e.stopPropagation()}
                onLoad={(e) => { const i = e.currentTarget; if (i && i.naturalWidth > 0) setProporcion(i.naturalHeight / i.naturalWidth) }}
                style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 4, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }} />
            </div>
          </div>
        </div>
        <ControlesZoom zona={zona} />
      </div>

      <div onClick={e => e.stopPropagation()}
        style={{ flexShrink: 0, textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 11.5, pointerEvents: 'none' }}>
        {t('components.VisorImagen.pellizca_o_toca_dos_veces', 'Pellizca o toca dos veces para ampliar · toca fuera para cerrar')}
      </div>
    </div>
  )
}
